export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  /** Large image for the service detail hero. */
  image: string;
  /** Smaller image for card/grid contexts (avoids shipping the hero-sized file to a ~355px card). */
  cardImage: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

// Imported through Vite so the files are content-hashed into immutable /assets/* (long cache).
import commercialImg from "@/assets/img/services/commercial-office-cleaning.webp";
import commercialCard from "@/assets/img/services/commercial-office-cleaning-card.webp";
import airbnbImg from "@/assets/img/services/airbnb-vacation-rental-cleaning.webp";
import airbnbCard from "@/assets/img/services/airbnb-vacation-rental-cleaning-card.webp";
import moveImg from "@/assets/img/services/move-in-move-out-cleaning.webp";
import moveCard from "@/assets/img/services/move-in-move-out-cleaning-card.webp";
import deepImg from "@/assets/img/services/deep-cleaning.webp";
import standardImg from "@/assets/img/services/standard-recurring-cleaning.webp";
import postImg from "@/assets/img/services/post-construction-cleaning.webp";
import upholsteryImg from "@/assets/img/services/upholstery-furniture-cleaning.webp";
import carpetImg from "@/assets/img/services/carpet-rug-tile-cleaning.webp";

const MEDIA =
  "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/jr-pro-cleaning-services/images";
const RESIDENTIAL = "1783631172991-396-IMG_0204.jpeg";

export const services: ServiceData[] = [
  {
    slug: "residential-house-cleaning",
    title: "Residential House Cleaning",
    subtitle: "Complete home & apartment cleaning, top to bottom",
    price: "Free Quote",
    image: `${MEDIA}/${RESIDENTIAL}?width=1200&quality=68`,
    cardImage: `${MEDIA}/${RESIDENTIAL}?width=600&height=375&resize=cover&quality=60`,
    description:
      "Our residential house cleaning covers every room of your home or apartment — kitchens, bathrooms, bedrooms, and living areas. From dusting and sanitizing surfaces to detailing baseboards and interior windows, the JR Pro team leaves your Tampa home genuinely clean, not just tidied. We bring our own supplies, follow a room-by-room checklist, and treat your home with the same care we'd give our own.",
    features: [
      "House & apartment cleaning",
      "Kitchen cleaning & degreasing",
      "Bathroom cleaning & sanitizing",
      "Dusting & sanitizing all surfaces",
      "Baseboard cleaning",
      "Interior window cleaning",
      "Cabinet cleaning (inside & out)",
      "Refrigerator cleaning",
      "Oven cleaning",
      "Laundry room cleaning",
    ],
    process: [
      { step: "Free Estimate", desc: "Tell us about your home — bedrooms, bathrooms, and priorities — and we'll send a clear quote." },
      { step: "Custom Checklist", desc: "We build a room-by-room plan around your home and any problem areas you point out." },
      { step: "Detailed Clean", desc: "Our team works through every room with professional products and a consistent system." },
      { step: "Final Walkthrough", desc: "We review the results with you so nothing is missed before we call it done." },
    ],
  },
  {
    slug: "commercial-office-cleaning",
    title: "Commercial & Office Cleaning",
    subtitle: "Clean, professional workspaces your team will notice",
    price: "Free Quote",
    image: commercialImg,
    cardImage: commercialCard,
    description:
      "First impressions matter — for your customers and your employees. JR Pro Cleaning Services keeps Tampa Bay offices, storefronts, and commercial spaces spotless with flexible scheduling that works around your business hours. From lobbies and workstations to restrooms and break rooms, we deliver consistent, reliable commercial cleaning you can set and forget.",
    features: [
      "Office & workstation cleaning",
      "Lobby & common area upkeep",
      "Restroom cleaning & sanitizing",
      "Break room & kitchenette cleaning",
      "Trash removal & liner replacement",
      "Floor care: vacuuming & mopping",
      "High-touch surface disinfecting",
      "After-hours & weekend scheduling",
    ],
    process: [
      { step: "Walkthrough", desc: "We visit your space, learn your expectations, and scope the work accurately." },
      { step: "Custom Proposal", desc: "You get a clear plan and price — nightly, weekly, or on-demand service." },
      { step: "Consistent Service", desc: "The same reliable process every visit, on a schedule that fits your business." },
      { step: "Quality Checks", desc: "We follow up regularly to make sure the standard never slips." },
    ],
  },
  {
    slug: "deep-cleaning",
    title: "Deep Cleaning",
    subtitle: "The detailed reset your home needs a few times a year",
    price: "Free Quote",
    image: deepImg,
    cardImage: deepImg,
    description:
      "A deep clean goes far beyond a standard cleaning. While a standard visit maintains your home week to week, a deep cleaning attacks built-up grime in the places routine cleanings don't reach — inside appliances, behind furniture, grout lines, vents, baseboards, and window tracks. It's the perfect reset before hosting guests, after a long stretch without professional help, or as a first visit before starting recurring service.",
    features: [
      "Everything in a standard cleaning, plus:",
      "Inside oven & refrigerator cleaning",
      "Baseboards, trim & door frames",
      "Interior windows & window tracks",
      "Cabinet fronts & inside cabinets",
      "Descaling showers, tubs & fixtures",
      "Vents, fans & light fixtures dusted",
      "Behind & under furniture (where accessible)",
    ],
    process: [
      { step: "Assess & Quote", desc: "We ask the right questions about your home's condition and send an honest quote." },
      { step: "Top-to-Bottom Plan", desc: "Every room gets a detailed checklist covering the spots standard cleans skip." },
      { step: "The Deep Clean", desc: "Our team works methodically from ceilings to baseboards with professional products." },
      { step: "Walkthrough", desc: "We inspect every room with you — the difference should be obvious." },
    ],
  },
  {
    slug: "standard-recurring-cleaning",
    title: "Standard & Recurring Cleaning",
    subtitle: "Weekly, bi-weekly, or monthly upkeep on your schedule",
    price: "Free Quote",
    image: standardImg,
    cardImage: standardImg,
    description:
      "Keep your home consistently clean without lifting a finger. Our standard cleaning covers all the essentials — kitchens, bathrooms, floors, and surfaces — and our recurring plans (weekly, bi-weekly, or monthly) keep it that way. Same team, same checklist, same result every visit, with priority scheduling for recurring clients across Tampa Bay.",
    features: [
      "Kitchen surfaces, sink & exterior appliances",
      "Bathroom scrubbing & sanitizing",
      "Dusting all accessible surfaces",
      "Vacuuming carpets & rugs",
      "Sweeping & mopping hard floors",
      "Mirrors & glass touch-ups",
      "Trash removal & bed making",
      "Weekly, bi-weekly & monthly plans",
    ],
    process: [
      { step: "Pick a Frequency", desc: "Weekly, bi-weekly, or monthly — we'll help you choose what fits your home." },
      { step: "First Visit", desc: "We start with a thorough baseline clean so every visit after stays consistent." },
      { step: "Recurring Service", desc: "Same checklist, same standard, every time — no reminders needed." },
      { step: "Easy Adjustments", desc: "Need to add a task or change your schedule? One call is all it takes." },
    ],
  },
  {
    slug: "move-in-move-out-cleaning",
    title: "Move-In / Move-Out Cleaning",
    subtitle: "Leave it spotless — or start fresh in a truly clean home",
    price: "Free Quote",
    image: moveImg,
    cardImage: moveCard,
    description:
      "Moving is stressful enough without scrubbing an empty house. Whether you're handing back keys and want your deposit protected, or moving into a home you want genuinely clean before the boxes arrive, our move-in/move-out cleaning covers everything: inside cabinets and appliances, bathrooms, floors, baseboards, and all the details landlords and new owners check first.",
    features: [
      "Full empty-home cleaning",
      "Inside all cabinets & drawers",
      "Inside oven & refrigerator",
      "Bathroom deep scrub & sanitizing",
      "Baseboards, doors & switch plates",
      "Interior windows & tracks",
      "Floors vacuumed & mopped",
      "Deposit-friendly detail work",
    ],
    process: [
      { step: "Schedule Around Your Move", desc: "We coordinate with your closing or lease dates — even on short notice." },
      { step: "Empty-Home Checklist", desc: "A specialized checklist built for vacant properties and landlord inspections." },
      { step: "Detail Clean", desc: "Cabinets, appliances, bathrooms, floors — every inch gets attention." },
      { step: "Move-In Ready", desc: "Walk into a home that's actually clean, or hand back keys with confidence." },
    ],
  },
  {
    slug: "airbnb-vacation-rental-cleaning",
    title: "Airbnb & Vacation Rental Cleaning",
    subtitle: "Five-star turnovers, every single guest",
    price: "Free Quote",
    image: airbnbImg,
    cardImage: airbnbCard,
    description:
      "Your reviews live and die by cleanliness. JR Pro Cleaning Services handles fast, reliable turnovers for Airbnb, VRBO, and vacation rentals across Tampa Bay — hotel-quality cleaning, fresh linens, restocked essentials, and photo-ready staging before every check-in. We work from your listing's standards and report any damage or low supplies, so you can host from anywhere.",
    features: [
      "Full turnover cleaning between guests",
      "Linen changes & bed staging",
      "Bathroom restock & sanitizing",
      "Kitchen reset & dish check",
      "Supply restocking & inventory notes",
      "Damage & maintenance reporting",
      "Photo-ready staging before check-in",
      "Flexible same-day scheduling",
    ],
    process: [
      { step: "Sync Your Calendar", desc: "We schedule around bookings automatically — checkout to check-in, handled." },
      { step: "Custom Turnover List", desc: "Your staging preferences and house standards become our checklist." },
      { step: "Turnover Clean", desc: "Clean, stage, restock, and photograph — ready before the next guest arrives." },
      { step: "Host Report", desc: "You get a quick status update with any damage or supply notes after every turn." },
    ],
  },
  {
    slug: "post-construction-cleaning",
    title: "Post-Construction Cleaning",
    subtitle: "From job site to move-in ready",
    price: "Free Quote",
    image: postImg,
    cardImage: postImg,
    description:
      "Construction dust gets everywhere — and it takes more than a shop vac to make a renovated space livable. Our post-construction cleaning removes fine dust from every surface, scrubs away adhesive and paint residue, details windows and fixtures, and leaves new builds and remodels across Tampa Bay truly ready to occupy. Ideal for contractors, builders, and homeowners finishing a renovation.",
    features: [
      "Fine dust removal from all surfaces",
      "Paint, adhesive & sticker residue removal",
      "Window & glass detailing",
      "Fixture, cabinet & appliance cleaning",
      "Vent & air return dusting",
      "Floor scrubbing & finishing",
      "Debris removal & final polish",
      "Contractor & builder scheduling",
    ],
    process: [
      { step: "Site Review", desc: "We assess the space and construction debris to quote the job accurately." },
      { step: "Rough Clean", desc: "Debris, stickers, and heavy dust go first so the detail work can begin." },
      { step: "Detail Clean", desc: "Every surface, fixture, and floor is cleaned and polished to move-in standard." },
      { step: "Final Inspection", desc: "We walk the space with you or your contractor for sign-off." },
    ],
  },
  {
    slug: "upholstery-furniture-cleaning",
    title: "Upholstery & Furniture Cleaning",
    subtitle: "Sofas, sectionals, mattresses & more — refreshed and sanitized",
    price: "Free Quote",
    image: upholsteryImg,
    cardImage: upholsteryImg,
    description:
      "Your furniture collects more than you think — dust, allergens, spills, and odors settle deep into the fabric. Our professional upholstery cleaning safely lifts dirt and stains from sofas, couches, sectionals, mattresses, dining chairs, and ottomans, restoring color and freshness without harsh chemicals. Most pieces are dry and ready to use the same day.",
    features: [
      "Sofa & couch cleaning",
      "Sectional cleaning",
      "Mattress cleaning & sanitizing",
      "Dining chair cleaning",
      "Ottoman & accent furniture cleaning",
      "Stain & spot treatment",
      "Odor neutralizing",
      "Fabric-safe, family-safe products",
    ],
    process: [
      { step: "Fabric Assessment", desc: "We identify the fabric type and test products so nothing damages your furniture." },
      { step: "Pre-Treatment", desc: "Stains, spots, and high-traffic areas get targeted treatment first." },
      { step: "Deep Extraction", desc: "Professional equipment lifts embedded dirt, allergens, and odors from the fibers." },
      { step: "Fast Dry & Review", desc: "We groom the fabric for fast drying and walk through the results with you." },
    ],
  },
  {
    slug: "carpet-rug-tile-cleaning",
    title: "Carpet, Rug, Tile & Sofa Cleaning",
    subtitle: "Carpets, area rugs, sofas, pet stains, and tile & grout — like new again",
    price: "Free Quote",
    image: carpetImg,
    cardImage: carpetImg,
    description:
      "Floors take the hardest beating in any home. Our professional floor care restores carpets, area rugs, and tile & grout throughout Tampa Bay — deep steam extraction for carpets, gentle care for area rugs, targeted pet odor and stain removal, and high-pressure tile and grout cleaning that brings back the original color you forgot was there.",
    features: [
      "Carpet deep cleaning & extraction",
      "Area rug cleaning",
      "Pet odor & stain removal",
      "Tile & grout deep cleaning",
      "High-traffic area restoration",
      "Deodorizing & sanitizing",
      "Fast-dry professional equipment",
      "Safe for kids & pets",
    ],
    process: [
      { step: "Floor Inspection", desc: "We assess carpet, rug, or tile condition and flag stains that need special care." },
      { step: "Pre-Treatment", desc: "Stains, pet spots, and grout lines get targeted solutions before the main clean." },
      { step: "Deep Clean", desc: "Truck-grade extraction and scrubbing pull out dirt that vacuums never touch." },
      { step: "Dry & Inspect", desc: "We speed the drying process and review every area with you before we leave." },
    ],
  },
];
